// src/pages/Fertilization/Fertilization.jsx
import React, {
    useState,
    useRef,
    useEffect,
    useCallback,
    useMemo
} from 'react';
import CommonMap            from '../../components/CommonMap/CommonMap';
import { useSocket }        from '../../context/SocketContext';
import MapDialog            from '../../components/MapDialog/MapDialog';
import axios                from 'axios';
import { API_BASE_PYTHON_SERVICE } from '../../utils/config';
import BarIndicator         from '../../components/BarIndicator/BarIndicator';

const getNumericProp = (props, names = []) => {
    for (const n of names) {
        if (props?.[n] !== undefined && props[n] !== null) {
            const v = parseFloat(props[n]);
            if (!Number.isNaN(v)) return v;
        }
    }
    return undefined;
};

const COLOR_DEFAULT = '#198C19';
const COLOR_LOW  = '#00c853';
const COLOR_MED  = '#ffeb3b';
const COLOR_HIGH = '#ff9800';
const COLOR_MAX  = '#ff1744';

const COLOR_CATEGORIA = {
    'Sobre aplicado': '#f44336',
    'Sobre aplico'  : '#f44336',
    'Bien aplicado' : '#4caf50',
    'Sub aplicado'  : '#ffeb3b',
};

const applyRange = (v, low, med, high) =>
    v < low ? COLOR_LOW : v < med ? COLOR_MED : v < high ? COLOR_HIGH : COLOR_MAX;

const Fertilization = ({
                           tipoAnalisis,
                           isFilterDialogOpen,
                           closeFilterDialog,
                           harvestGroupings,
                           selectedHarvestGrouping,
                           setSelectedHarvestGrouping,
                           setTipoMapeo,
                           polygonsData,
                           onAreasCalculated,
                           idAnalisis,
                           setNorthWestCoords,
                           setSouthEastCoords,
                           setCoordinateSystem,
                           mapRef,
                           setFertilizacionShapefileUrl,
                           setLabelsAndColors
                       }) => {
    const workerRef   = useRef(null);
    const [zoom]      = useState(3);
    const [mapCenter] = useState([0, 0]);

    const [polygons,        setPolygons]        = useState([]);
    const [coloredPolygons, setColoredPolygons] = useState([]);
    const [lines,           setLines]           = useState([]);

    // Shapefile
    const [shapefileUrl, setShapefileUrl] = useState(null);

    const { socket, socketSessionID } = useSocket() || {};

    const [availableFilters, setAvailableFilters] = useState({
        autoPilot: false, autoTracket: false, modeCutterBase: false,
        speed: false, gpsQuality: false, fuel: false, rpm: false,
        cutterBase: false, altitude: false, realDose: false, hdop: false,
        p1drKg: false, p1drL: false, p1dtKg: false, p1dtL: false, p2dr: false,
        categoria: false,
    });

    const [filterVelocidad,  setFilterVelocidad ] = useState(false);
    const [filterAltura,     setFilterAltura    ] = useState(false);
    const [filterHDOP,       setFilterHDOP      ] = useState(false);
    const [filterP1DR,       setFilterP1DR      ] = useState(false);
    const [filterP1DT,       setFilterP1DT      ] = useState(false);
    const [filterP2DR,       setFilterP2DR      ] = useState(false);
    const [filterCategoria,  setFilterCategoria ] = useState(false);

    const [lowVelocidad,  setLowVelocidad]  = useState(0);
    const [medVelocidad,  setMedVelocidad]  = useState(0);
    const [highVelocidad, setHighVelocidad] = useState(0);

    const [lowAltura,  setLowAltura]  = useState(0);
    const [medAltura,  setMedAltura]  = useState(0);
    const [highAltura, setHighAltura] = useState(0);

    const [lowHDOP,  setLowHDOP]  = useState(0);
    const [medHDOP,  setMedHDOP]  = useState(0);
    const [highHDOP, setHighHDOP] = useState(0);

    const [lowP1DR,  setLowP1DR]  = useState(0);
    const [medP1DR,  setMedP1DR]  = useState(0);
    const [highP1DR, setHighP1DR] = useState(0);

    const [lowP1DT,  setLowP1DT]  = useState(0);
    const [medP1DT,  setMedP1DT]  = useState(0);
    const [highP1DT, setHighP1DT] = useState(0);

    const [lowP2DR,  setLowP2DR]  = useState(0);
    const [medP2DR,  setMedP2DR]  = useState(0);
    const [highP2DR, setHighP2DR] = useState(0);

    const [categoryRanges, setCategoryRanges] = useState({ pos: 0, neg: 0 });
    const [manualCategoryRanges, setManualCategoryRanges] = useState(null);

    const [activeFilter, setActiveFilter] = useState(null);

    const handleLabelClick = useCallback((text, color) => {
        if (typeof setLabelsAndColors === 'function') {
            setLabelsAndColors({ text, color });
        }
    }, [setLabelsAndColors]);

    const legendRanges = useMemo(() => ({
        speed : { low: lowVelocidad,  med: medVelocidad,  high: highVelocidad },
        altura: { low: lowAltura,     med: medAltura,     high: highAltura },
        hdop  : { low: lowHDOP,       med: medHDOP,       high: highHDOP },
        p1dr  : { low: lowP1DR,       med: medP1DR,       high: highP1DR },
        p1dt  : { low: lowP1DT,       med: medP1DT,       high: highP1DT },
        p2dr  : { low: lowP2DR,       med: medP2DR,       high: highP2DR },
    }), [
        lowVelocidad, medVelocidad, highVelocidad,
        lowAltura,    medAltura,    highAltura,
        lowHDOP,      medHDOP,      highHDOP,
        lowP1DR,      medP1DR,      highP1DR,
        lowP1DT,      medP1DT,      highP1DT,
        lowP2DR,      medP2DR,      highP2DR
    ]);

    const readManualFertRanges = () => {
        try {
            const raw = localStorage.getItem('fertilizacion_rangos');
            if (!raw) return null;
            const arr = JSON.parse(raw);
            if (!Array.isArray(arr) || arr.length < 3) return null;
            const [min, mid, max] = arr.map(Number);
            if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
            return { min, mid: Number.isFinite(mid) ? mid : null, max };
        }catch {
            return null;
        }
    };

    useEffect(() => {

        try {
            const worker = new Worker('Workers/dataWorker.js');
            workerRef.current = worker;

            worker.onmessage = (e) => {
                try {
                    if (!e?.data) {
                        return;
                    }

                    if (e.data.action !== 'geoJsonDataProcessed') {
                        return;
                    }

                    const shpFromRoot = e.data?.shapefileUrl;
                    const shpFromData = e.data?.data?.shapefileUrl;
                    const shp = shpFromRoot ?? shpFromData ?? null;

                    const polygons = e.data?.data?.polygons || [];
                    const lines    = e.data?.data?.lines    || [];

                    setPolygons(polygons);
                    setLines(lines);
                    setShapefileUrl(shp);

                    if (setFertilizacionShapefileUrl) {
                        setFertilizacionShapefileUrl(shp);
                    }
                } catch (error) {
                    console.error('❌ [Worker.onmessage] Error procesando mensaje del worker:', error, ' | Evento:', e);
                }
            };

            worker.onerror = (error) => {
                console.error('❌ [Worker.onerror] Error en el Worker "Workers/dataWorker.js":', error);
            };
        } catch (err) {
            console.error('❌ [useEffect] Error creando el Worker:', err);
        }

        let channel = null;

        const handleUpdateGeoJSONLayer = (msg) => {
            try {
                if (!workerRef.current) {
                    return;
                }

                const rawData = msg?.geojsonData ?? msg;
                let payload;

                if (tipoAnalisis === 'FERTILIZACION') {
                    const meta =
                        rawData?.geojsonDataFiltradas ??
                        rawData?.geojsonDataNoFiltradas ??
                        rawData;

                    let fertUrl = null;
                    if (typeof meta === 'string') {
                        fertUrl = meta;
                    } else {
                        fertUrl =
                            meta?.geojsonUrl ??
                            meta?.url ??
                            meta?.href ??
                            null;
                    }

                    const shapefileUrl =
                        meta?.shapefileUrl ??
                        meta?.shapefile_url ??
                        meta?.shapeFileUrl ??
                        rawData?.shapefileUrl ??
                        rawData?.shapefile_url ??
                        rawData?.shapeFileUrl ??
                        null;

                    payload = {
                        geojsonDataNoFiltradas: fertUrl,
                        shapefileUrl,
                    };
                } else {
                    payload = rawData;
                }

                const messageToWorker = {
                    action: 'processGeoJsonData',
                    geojsonData: payload,
                    type: tipoAnalisis,
                };

                workerRef.current.postMessage(messageToWorker);
            } catch (err) {
                console.error('❌ [Socket.handleUpdateGeoJSONLayer] Error procesando mensaje del socket:', err, ' | msg:', msg);
            }
        };

        if (socket && socketSessionID) {
            channel = `${socketSessionID}:updateGeoJSONLayer`;

            try {
                socket.on(channel, handleUpdateGeoJSONLayer);
            } catch (err) {
                console.error('❌ [useEffect] Error registrando listener en socket.on:', err);
            }
        }

        return () => {
            try {
                if (workerRef.current) {
                    workerRef.current.terminate();
                    workerRef.current = null;
                }
            } catch (err) {
                console.error('❌ [cleanup] Error al terminar Worker:', err);
            }

            try {
                if (socket && channel && socket.off) {
                    socket.off(channel, handleUpdateGeoJSONLayer);
                }
            } catch (err) {
                console.error('❌ [cleanup] Error al intentar remover listener del socket:', err);
            }
        };
    }, [socket, socketSessionID, tipoAnalisis, setFertilizacionShapefileUrl]);


    useEffect(() => {
        if (setFertilizacionShapefileUrl) {
            setFertilizacionShapefileUrl(shapefileUrl);
        }
    }, [shapefileUrl, setFertilizacionShapefileUrl]);

    useEffect(() => {
        if (!Array.isArray(polygons) || polygons.length === 0) return;

        let hasSpeed=false,hasAlt=false,hasHdop=false,
            hasP1drKg=false,hasP1drL=false,
            hasP1dtKg=false,hasP1dtL=false,
            hasP2dr=false,
            hasCategoria=false;

        polygons.forEach(({ properties = {} }) => {
            if (getNumericProp(properties,['Velocidad_']))             hasSpeed = true;
            if (getNumericProp(properties,['Altura_m_']))              hasAlt   = true;
            if (getNumericProp(properties,['HDOP']))                   hasHdop  = true;
            if (getNumericProp(properties,['P1_DR_Kg_H']))             hasP1drKg = true;
            if (getNumericProp(properties,['P1_DR_L_Ha']))             hasP1drL  = true;
            if (getNumericProp(properties,['P1_DT_Kg_H']))             hasP1dtKg = true;
            if (getNumericProp(properties,['P1_DT_L_Ha']))             hasP1dtL  = true;
            if (getNumericProp(properties,['P2_DR_L_Ha']))             hasP2dr   = true;
            if (properties?.CATEGORIA)                                 hasCategoria = true;
        });

        if (hasP1drL) hasP1drKg = false;
        if (hasP1dtL) hasP1dtKg = false;

        setAvailableFilters(prev => ({
            ...prev,
            speed: hasSpeed, altitude: hasAlt, hdop: hasHdop,
            p1drKg: hasP1drKg, p1drL: hasP1drL,
            p1dtKg: hasP1dtKg, p1dtL: hasP1dtL,
            p2dr: hasP2dr,
            categoria: hasCategoria,
        }));
    }, [polygons]);

    // 4) Calcular rangos dinámicos categoría
    useEffect(() => {
        if (!Array.isArray(polygons) || polygons.length === 0) {
            setCategoryRanges({ pos: 0, neg: 0 });
            return;
        }
        if (filterCategoria && manualCategoryRanges?.min != null && manualCategoryRanges?.max != null) {
            setCategoryRanges({ pos: manualCategoryRanges.max, neg: -manualCategoryRanges.min });
            return;
        }
        let minPos = Infinity;
        let maxNeg = -Infinity;
        polygons.forEach(({ properties = {} }) => {
            const v = getNumericProp(properties, ['DIFERENCIA_REAL','DIFERENCIA','DIF','VARIACION','VAR']);
            if (v === undefined) return;
            if (v > 0 && v < minPos) minPos = v;
            if (v < 0 && v > maxNeg) maxNeg = v;
        });
        if (minPos === Infinity) minPos = 0;
        if (maxNeg === -Infinity) maxNeg = 0;
        setCategoryRanges({ pos: minPos, neg: maxNeg });
    }, [polygons, filterCategoria, manualCategoryRanges]);

    // 5) Cálculo de áreas
    useEffect(() => {
        if (!Array.isArray(polygons) || polygons.length === 0 ||
            !Array.isArray(polygonsData) || polygonsData.length === 0) {
            return;
        }

        const calculateAreas = async () => {
            try {
                const polygonsForRequest = polygons
                    .filter(p => p?.properties?.INTERSECCION !== 'YES')
                    .map(p => {
                        const ring = p.geometry?.coordinates?.[0] || [];
                        const coordsLonLat = ring.map(([lat, lon]) => [lon, lat]);
                        return {
                            geometry: {
                                type: 'Polygon',
                                coordinates: [coordsLonLat],
                            },
                        };
                    });

                const analisisId = idAnalisis?.data?.ID_ANALISIS;

                const requestData = {
                    id_analisis: analisisId,
                    bufferedLines: [],
                    polygons: polygonsForRequest,
                    polygonsData: polygonsData.map(pd => ({
                        type: pd.type,
                        geometry: pd.geometry,
                        properties: pd.properties,
                    })),
                    tipo_analisis: 'fertilization',
                };

                const response = await axios.post(
                    `${API_BASE_PYTHON_SERVICE}mapping/calculate_areas`,
                    requestData
                );

                if (response.status === 200 && response.data?.data) {
                    const {
                        areaSobreAplicada,
                        areaAplicada,
                        areaNoAplicada,
                        porcentajeDeVariacion,
                        areaAplicadaEnCalles,
                        areaAplicadaFueraDelLote,
                        dosisTotalDiferencia,
                        dosisTotalRealAplicada,
                        areaTotalLote,
                    } = response.data.data;

                    if (typeof onAreasCalculated === 'function') {
                        onAreasCalculated({
                            areaSobreAplicada,
                            areaAplicada,
                            areaNoAplicada,
                            porcentajeDeVariacion,
                            areaAplicadaEnCalles,
                            areaAplicadaFueraDelLote,
                            dosisTotalRealAplicada,
                            dosisTotalDiferencia,
                            areaTotalLote,
                        });
                    }
                }
            } catch (error) {
                console.error('Error al calcular áreas:', error);
            }
        };

        const timeoutRef = setTimeout(calculateAreas, 500);
        return () => clearTimeout(timeoutRef);
    }, [polygons, polygonsData, lines, onAreasCalculated, idAnalisis]);

    // 6) Colorear polígonos
    useEffect(() => {
        if (!Array.isArray(polygons)) return;

        const updated = polygons.map(poly => {
            const props = { ...poly.properties };
            let color   = COLOR_DEFAULT;

            if (filterCategoria) {
                const manual = manualCategoryRanges;
                if (manual?.min != null && manual?.max != null) {
                    const v = getNumericProp(props, ['DIFERENCIA_REAL','DIFERENCIA','DIF','VARIACION','VAR']);
                    if (v !== undefined) {
                        const categoria =
                            v > manual.max ? 'Sobre aplicado'
                                : v < -manual.min ? 'Sub aplicado'
                                    : 'Bien aplicado';
                        color = COLOR_CATEGORIA[categoria] || COLOR_DEFAULT;
                    } else {
                        color = COLOR_CATEGORIA[props.CATEGORIA] || COLOR_DEFAULT;
                    }
                } else {
                    color = COLOR_CATEGORIA[props.CATEGORIA] || COLOR_DEFAULT;
                }
            }

            if (filterVelocidad) {
                const v = getNumericProp(props,['Velocidad_']);
                if (v !== undefined) color = applyRange(v, lowVelocidad, medVelocidad, highVelocidad);
            }
            if (filterAltura) {
                const v = getNumericProp(props,['Altura_m_']);
                if (v !== undefined) color = applyRange(v, lowAltura, medAltura, highAltura);
            }
            if (filterHDOP) {
                const v = getNumericProp(props,['HDOP']);
                if (v !== undefined) color = applyRange(v, lowHDOP, medHDOP, highHDOP);
            }
            if (filterP1DR) {
                const v = getNumericProp(props,['P1_DR_Kg_H','P1_DR_L_Ha']);
                if (v !== undefined) color = applyRange(v, lowP1DR, medP1DR, highP1DR);
            }
            if (filterP1DT) {
                const v = getNumericProp(props,['P1_DT_Kg_H','P1_DT_L_Ha']);
                if (v !== undefined) color = applyRange(v, lowP1DT, medP1DT, highP1DT);
            }
            if (filterP2DR) {
                const v = getNumericProp(props,['P2_DR_L_Ha']);
                if (v !== undefined) color = applyRange(v, lowP2DR, medP2DR, highP2DR);
            }

            return { ...poly, properties: { ...props, color } };
        });
        setColoredPolygons(updated);
    }, [
        polygons,
        filterCategoria, filterVelocidad, filterAltura, filterHDOP,
        filterP1DR, filterP1DT, filterP2DR,
        lowVelocidad, medVelocidad, highVelocidad,
        lowAltura, medAltura, highAltura,
        lowHDOP, medHDOP, highHDOP,
        lowP1DR, medP1DR, highP1DR,
        lowP1DT, medP1DT, highP1DT,
        lowP2DR, medP2DR, highP2DR
    ]);

    useEffect(() => {
        const update = () => setManualCategoryRanges(readManualFertRanges());
        update();
        window.addEventListener('fertilizacion:rangos-actualizados', update);
        return () => window.removeEventListener('fertilizacion:rangos-actualizados', update);
    }, [polygons]);

    const toggle = (setter, key) =>
        setter(prev => { const next = !prev; setActiveFilter(next ? key : null); return next; });

    const handleToggleFilter = useCallback(name => {
        switch (name) {
            case 'VELOCIDAD': case 'Velocidad_': return toggle(setFilterVelocidad, 'speed');
            case 'ALTURA'   : case 'Altura_m_' : return toggle(setFilterAltura   , 'altura');
            case 'HDOP'                           : return toggle(setFilterHDOP     , 'hdop');
            case 'P1_DR_Kg_H': case 'P1_DR_L_Ha' : return toggle(setFilterP1DR   , 'p1dr');
            case 'P1_DT_Kg_H': case 'P1_DT_L_Ha' : return toggle(setFilterP1DT   , 'p1dt');
            case 'P2_DR_L_Ha'                    : return toggle(setFilterP2DR   , 'p2dr');
            case 'CATEGORIA'                     : return toggle(setFilterCategoria, 'categoria');
            default: break;
        }
    }, []);

    return (
        <>
            <CommonMap
                center={mapCenter}
                zoom={zoom}
                polygons={coloredPolygons}
                setPolygons={setPolygons}
                lines={lines}
                points={[]}
                hullPolygon={null}
                nonIntersectedAreas={[]}
                bufferedLines={[]}
                bufferedIntersections={[]}
                onLineHover={() => {}}
                onLineMouseOut={() => {}}
                onLineClick={() => {}}
                polygonProperties={[]}
                showIntersections={!(activeFilter || selectedHarvestGrouping)}
                mapRef={mapRef}
                userId={null}
                stretchPoints={[]}
                lineasNoFiltradas={[]}
                highlightedLote={null}
                polygonsData={polygonsData}
                activeLotes={[]}
                onLeaveLote={() => {}}
                onSelectLote={() => {}}
                onHoverLote={() => {}}
                areasSuperpuestas={[]}
                setImgLaflet={() => {}}
                polygonsLayerRef={useRef(null)}
                setLines={setLines}
                popupInfo={null}
                setPopupInfo={() => {}}
                isEditing={false}
                setNorthWestCoords={setNorthWestCoords}
                setSouthEastCoords={setSouthEastCoords}
                setCoordinateSystem={setCoordinateSystem}
            />
            {!activeFilter && (
                <BarIndicator
                    filterType="fertilizacionDefault"
                    isHistory={false}
                    onLabelClick={handleLabelClick}
                    setLabelsAndColors={setLabelsAndColors}
                />
            )}

            {activeFilter && (
                <BarIndicator
                    filterType={activeFilter}
                    categoryRanges={categoryRanges}
                    ranges={legendRanges}
                    isHistory={false}
                    onLabelClick={handleLabelClick}
                    setLabelsAndColors={setLabelsAndColors}
                />
            )}

            <MapDialog
                isOpen={!!isFilterDialogOpen}
                onClose={() => typeof closeFilterDialog === 'function' && closeFilterDialog()}
                availableFilters={availableFilters}

                /* velocidad */
                lowSpeed={lowVelocidad}   medSpeed={medVelocidad}   highSpeed={highVelocidad}
                setLowSpeed={setLowVelocidad}   setMedSpeed={setMedVelocidad}   setHighSpeed={setHighVelocidad}

                /* altura */
                lowAltitude={lowAltura}   medAltitude={medAltura}   highAltitude={highAltura}
                setLowAltitude={setLowAltura}   setMedAltitude={setMedAltura}   setHighAltitude={setHighAltura}

                /* hdop */
                lowHDOP={lowHDOP}   medHDOP={medHDOP}   highHDOP={highHDOP}
                setLowHDOP={setLowHDOP}   setMedHDOP={setMedHDOP}   setHighHDOP={setHighHDOP}

                /* p1dr */
                lowP1DR={lowP1DR}   medP1DR={medP1DR}   highP1DR={highP1DR}
                setLowP1DR={setLowP1DR}   setMedP1DR={setMedP1DR}   setHighP1DR={setHighP1DR}

                /* p1dt */
                lowP1DT={lowP1DT}   medP1DT={medP1DT}   highP1DT={highP1DT}
                setLowP1DT={setLowP1DT}   setMedP1DT={setMedP1DT}   setHighP1DT={setHighP1DT}

                /* p2dr */
                lowP2DR={lowP2DR}   medP2DR={medP2DR}   highP2DR={highP2DR}
                setLowP2DR={setLowP2DR}   setMedP2DR={setMedP2DR}   setHighP2DR={setHighP2DR}

                handleToggleFilter={handleToggleFilter}
                setTipoMapeo={setTipoMapeo}
                harvestGroupings={harvestGroupings}
                selectedHarvestGrouping={selectedHarvestGrouping}
                onSelectHarvestGrouping={setSelectedHarvestGrouping}
            />
        </>
    );
};

export default Fertilization;
